// One configuration store behind both OCPP views of it:
//  - OCPP 1.6 configuration keys (GetConfiguration / ChangeConfiguration), and
//  - the OCPP 2.x device model (GetVariables / SetVariables / GetBaseReport / NotifyReport).
// A 1.6 key and its 2.x variable are the same entry, so a value set through one version is read
// back through the other. The key <-> variable table is the one ocpp-server translates with
// (OCPP 2.0.1 part 2 appendix, migration from 1.6 configuration keys).

export type DataType = "string" | "integer" | "decimal" | "boolean" | "dateTime" | "OptionList" | "MemberList" | "SequenceList";
export type Mutability = "ReadOnly" | "ReadWrite" | "WriteOnly";

export interface ComponentRef {
  name: string;
  instance?: string;
  evse?: { id: number; connectorId?: number };
}

export interface VariableRef {
  name: string;
  instance?: string;
}

export interface ConfigEntry {
  /** OCPP 1.6 configuration key, when the entry exists in 1.6. */
  key?: string;
  component: ComponentRef;
  variable: VariableRef;
  value: string;
  dataType: DataType;
  mutability: Mutability;
  /** OCPP 2.x only: entry not visible through GetConfiguration. */
  ocpp2Only?: boolean;
  /** OCPP 1.6 only: entry has no device-model variable. */
  ocpp16Only?: boolean;
  /** Changing it only takes effect after a reboot. */
  rebootRequired?: boolean;
  valuesList?: string;
}

export interface SettingsKey {
  key: string;
  readonly: boolean;
  value: string | number | boolean;
}

type Def = Omit<ConfigEntry, "value"> & { defaultValue: string };

const cv = (component: string, variable: string, variableInstance?: string) => ({
  component: { name: component },
  variable: { name: variable, ...(variableInstance && { instance: variableInstance }) },
});

/** Measurands the simulated meter can sample (the configurable *Data keys accept these). */
export const SUPPORTED_MEASURANDS = [
  "Energy.Active.Import.Register",
  "Energy.Active.Export.Register",
  "Energy.Reactive.Import.Register",
  "Energy.Active.Import.Interval",
  "Power.Active.Import",
  "Power.Offered",
  "Power.Factor",
  "Current.Import",
  "Current.Offered",
  "Voltage",
  "Frequency",
  "Temperature",
  "SoC",
];
const MEASURANDS = SUPPORTED_MEASURANDS.join(",");
const FEATURE_PROFILES = "Core,FirmwareManagement,LocalAuthListManagement,Reservation,SmartCharging,RemoteTrigger";

/**
 * Every entry the simulator knows, with its default value. Mutability follows the OCPP 1.6 key
 * tables (section 9) and the 2.0.1 part 2 appendix; `valuesList` limits MemberList / OptionList
 * values the way a real charge point rejects what it cannot do.
 */
const DEFINITIONS: Def[] = [
  // --- 1.6 keys with a device-model equivalent -------------------------------------------
  { key: "AllowOfflineTxForUnknownId", ...cv("AuthCtrlr", "OfflineTxForUnknownIdEnabled"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "false" },
  { key: "AuthorizationCacheEnabled", ...cv("AuthCacheCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "false" },
  { key: "AuthorizationKey", ...cv("SecurityCtrlr", "BasicAuthPassword"), dataType: "string", mutability: "WriteOnly", defaultValue: "" },
  { key: "AuthorizeRemoteTxRequests", ...cv("AuthCtrlr", "AuthorizeRemoteStart"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "false" },
  { key: "ChargeProfileMaxStackLevel", ...cv("SmartChargingCtrlr", "ProfileStackLevel"), dataType: "integer", mutability: "ReadOnly", defaultValue: "8" },
  { key: "ClockAlignedDataInterval", ...cv("AlignedDataCtrlr", "Interval"), dataType: "integer", mutability: "ReadWrite", defaultValue: "900" },
  { key: "ConnectionTimeOut", ...cv("TxCtrlr", "EVConnectionTimeOut"), dataType: "integer", mutability: "ReadWrite", defaultValue: "60" },
  { key: "HeartbeatInterval", ...cv("OCPPCommCtrlr", "HeartbeatInterval"), dataType: "integer", mutability: "ReadWrite", defaultValue: "300" },
  { key: "LocalAuthListEnabled", ...cv("LocalAuthListCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true" },
  { key: "LocalAuthorizeOffline", ...cv("AuthCtrlr", "LocalAuthorizeOffline"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "false" },
  { key: "LocalPreAuthorize", ...cv("AuthCtrlr", "LocalPreAuthorize"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "false" },
  { key: "MeterValueSampleInterval", ...cv("SampledDataCtrlr", "TxUpdatedInterval"), dataType: "integer", mutability: "ReadWrite", defaultValue: "60" },
  { key: "MaxEnergyOnInvalidId", ...cv("TxCtrlr", "MaxEnergyOnInvalidId"), dataType: "integer", mutability: "ReadWrite", defaultValue: "0" },
  { key: "MeterValuesAlignedData", ...cv("AlignedDataCtrlr", "Measurands"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "Energy.Active.Import.Register", valuesList: MEASURANDS },
  { key: "MeterValuesSampledData", ...cv("SampledDataCtrlr", "TxUpdatedMeasurands"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "Energy.Active.Import.Register,Power.Active.Import,Voltage,Current.Import,SoC", valuesList: MEASURANDS },
  { key: "ResetRetries", ...cv("OCPPCommCtrlr", "ResetRetries"), dataType: "integer", mutability: "ReadWrite", defaultValue: "3" },
  { key: "SecurityProfile", ...cv("SecurityCtrlr", "SecurityProfile"), dataType: "integer", mutability: "ReadWrite", defaultValue: "0", rebootRequired: true },
  { key: "StopTransactionOnEVSideDisconnect", ...cv("TxCtrlr", "StopTxOnEVSideDisconnect"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true" },
  { key: "StopTransactionOnInvalidId", ...cv("TxCtrlr", "StopTxOnInvalidId"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true" },
  { key: "StopTxnAlignedData", ...cv("AlignedDataCtrlr", "TxEndedMeasurands"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "", valuesList: MEASURANDS },
  { key: "StopTxnSampledData", ...cv("SampledDataCtrlr", "TxEndedMeasurands"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "Energy.Active.Import.Register", valuesList: MEASURANDS },
  { key: "TransactionMessageAttempts", ...cv("OCPPCommCtrlr", "MessageAttempts", "TransactionEvent"), dataType: "integer", mutability: "ReadWrite", defaultValue: "3" },
  { key: "TransactionMessageRetryInterval", ...cv("OCPPCommCtrlr", "MessageAttemptInterval", "TransactionEvent"), dataType: "integer", mutability: "ReadWrite", defaultValue: "60" },
  { key: "UnlockConnectorOnEVSideDisconnect", ...cv("OCPPCommCtrlr", "UnlockOnEVSideDisconnect"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true" },
  { key: "WebSocketPingInterval", ...cv("OCPPCommCtrlr", "WebSocketPingInterval"), dataType: "integer", mutability: "ReadWrite", defaultValue: "30" },
  // --- 1.6-only keys -----------------------------------------------------------------------
  { key: "GetConfigurationMaxKeys", ...cv("DeviceDataCtrlr", "ItemsPerMessage", "GetConfiguration"), dataType: "integer", mutability: "ReadOnly", defaultValue: "100", ocpp16Only: true },
  { key: "NumberOfConnectors", ...cv("ChargingStation", "NumberOfConnectors"), dataType: "integer", mutability: "ReadOnly", defaultValue: "2", ocpp16Only: true },
  { key: "ConnectorPhaseRotation", ...cv("EVSE", "PhaseRotation"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "0.RST", ocpp16Only: true },
  { key: "SupportedFeatureProfiles", ...cv("ChargingStation", "SupportedFeatureProfiles"), dataType: "MemberList", mutability: "ReadOnly", defaultValue: FEATURE_PROFILES, ocpp16Only: true },
  { key: "SupportedFileTransferProtocols", ...cv("OCPPCommCtrlr", "FileTransferProtocols"), dataType: "MemberList", mutability: "ReadOnly", defaultValue: "HTTP,HTTPS" },
  { key: "LocalAuthListMaxLength", ...cv("LocalAuthListCtrlr", "Entries"), dataType: "integer", mutability: "ReadOnly", defaultValue: "100", ocpp16Only: true },
  { key: "SendLocalListMaxLength", ...cv("LocalAuthListCtrlr", "ItemsPerMessage"), dataType: "integer", mutability: "ReadOnly", defaultValue: "100", ocpp16Only: true },
  { key: "ReserveConnectorZeroSupported", ...cv("ReservationCtrlr", "NonEvseSpecific"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "false", ocpp16Only: true },
  { key: "ChargingScheduleAllowedChargingRateUnit", ...cv("SmartChargingCtrlr", "RateUnit"), dataType: "MemberList", mutability: "ReadOnly", defaultValue: "Current,Power", ocpp16Only: true },
  { key: "ChargingScheduleMaxPeriods", ...cv("SmartChargingCtrlr", "PeriodsPerSchedule"), dataType: "integer", mutability: "ReadOnly", defaultValue: "24", ocpp16Only: true },
  { key: "MaxChargingProfilesInstalled", ...cv("SmartChargingCtrlr", "Entries", "ChargingProfiles"), dataType: "integer", mutability: "ReadOnly", defaultValue: "16", ocpp16Only: true },
  { key: "ConnectorSwitch3to1PhaseSupported", ...cv("SmartChargingCtrlr", "Phases3to1"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "false" },
  // Core keys without a device-model equivalent
  { key: "BlinkRepeat", ...cv("ChargingStation", "BlinkRepeat"), dataType: "integer", mutability: "ReadWrite", defaultValue: "3", ocpp16Only: true },
  { key: "LightIntensity", ...cv("ChargingStation", "LightIntensity"), dataType: "integer", mutability: "ReadWrite", defaultValue: "50", ocpp16Only: true },
  { key: "MinimumStatusDuration", ...cv("ChargingStation", "MinimumStatusDuration"), dataType: "integer", mutability: "ReadWrite", defaultValue: "0", ocpp16Only: true },
  { key: "ConnectorPhaseRotationMaxLength", ...cv("EVSE", "PhaseRotationMaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "3", ocpp16Only: true },
  { key: "MeterValuesAlignedDataMaxLength", ...cv("AlignedDataCtrlr", "MeasurandsMaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "13", ocpp16Only: true },
  { key: "MeterValuesSampledDataMaxLength", ...cv("SampledDataCtrlr", "MeasurandsMaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "13", ocpp16Only: true },
  { key: "StopTxnAlignedDataMaxLength", ...cv("AlignedDataCtrlr", "TxEndedMeasurandsMaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "13", ocpp16Only: true },
  { key: "StopTxnSampledDataMaxLength", ...cv("SampledDataCtrlr", "TxEndedMeasurandsMaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "13", ocpp16Only: true },
  { key: "SupportedFeatureProfilesMaxLength", ...cv("ChargingStation", "SupportedFeatureProfilesMaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "6", ocpp16Only: true },
  { key: "CpoName", ...cv("SecurityCtrlr", "OrganizationName"), dataType: "string", mutability: "ReadWrite", defaultValue: "EVsafe" },
  { key: "CertificateSignedMaxChainSize", ...cv("SecurityCtrlr", "MaxCertificateChainSize"), dataType: "integer", mutability: "ReadOnly", defaultValue: "10000" },
  { key: "CertificateStoreMaxLength", ...cv("SecurityCtrlr", "CertificateEntries"), dataType: "integer", mutability: "ReadOnly", defaultValue: "50" },
  { key: "AdditionalRootCertificateCheck", ...cv("SecurityCtrlr", "AdditionalRootCertificateCheck"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "false" },
  // --- 2.x-only device model variables -------------------------------------------------------
  { ...cv("DeviceDataCtrlr", "ItemsPerMessage", "GetVariables"), dataType: "integer", mutability: "ReadOnly", defaultValue: "50", ocpp2Only: true },
  { ...cv("DeviceDataCtrlr", "ItemsPerMessage", "SetVariables"), dataType: "integer", mutability: "ReadOnly", defaultValue: "50", ocpp2Only: true },
  { ...cv("DeviceDataCtrlr", "BytesPerMessage", "GetVariables"), dataType: "integer", mutability: "ReadOnly", defaultValue: "65000", ocpp2Only: true },
  { ...cv("DeviceDataCtrlr", "BytesPerMessage", "SetVariables"), dataType: "integer", mutability: "ReadOnly", defaultValue: "65000", ocpp2Only: true },
  { ...cv("AuthCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true", ocpp2Only: true },
  { ...cv("OCPPCommCtrlr", "OfflineThreshold"), dataType: "integer", mutability: "ReadWrite", defaultValue: "60", ocpp2Only: true },
  { ...cv("OCPPCommCtrlr", "NetworkConfigurationPriority"), dataType: "SequenceList", mutability: "ReadWrite", defaultValue: "0", ocpp2Only: true },
  { ...cv("OCPPCommCtrlr", "NetworkProfileConnectionAttempts"), dataType: "integer", mutability: "ReadWrite", defaultValue: "3", ocpp2Only: true },
  { ...cv("OCPPCommCtrlr", "MessageTimeout", "Default"), dataType: "integer", mutability: "ReadOnly", defaultValue: "30", ocpp2Only: true },
  { ...cv("SampledDataCtrlr", "TxEndedInterval"), dataType: "integer", mutability: "ReadWrite", defaultValue: "0", ocpp2Only: true },
  { ...cv("SampledDataCtrlr", "TxStartedMeasurands"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "Energy.Active.Import.Register", valuesList: MEASURANDS, ocpp2Only: true },
  { ...cv("SampledDataCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true", ocpp2Only: true },
  { ...cv("AlignedDataCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadWrite", defaultValue: "true", ocpp2Only: true },
  { ...cv("AlignedDataCtrlr", "TxEndedInterval"), dataType: "integer", mutability: "ReadWrite", defaultValue: "0", ocpp2Only: true },
  { ...cv("DeviceDataCtrlr", "ItemsPerMessage", "GetReport"), dataType: "integer", mutability: "ReadOnly", defaultValue: "20", ocpp2Only: true },
  { ...cv("DeviceDataCtrlr", "BytesPerMessage", "GetReport"), dataType: "integer", mutability: "ReadOnly", defaultValue: "65000", ocpp2Only: true },
  { ...cv("ClockCtrlr", "TimeSource"), dataType: "SequenceList", mutability: "ReadWrite", defaultValue: "Heartbeat", valuesList: "Heartbeat,NTP,GPS,RealTimeClock,MobileNetwork,RadioTimeTransmitter", ocpp2Only: true },
  { ...cv("SecurityCtrlr", "CertificateEntries", "MaxLength"), dataType: "integer", mutability: "ReadOnly", defaultValue: "50", ocpp2Only: true },
  { ...cv("SecurityCtrlr", "Identity"), dataType: "string", mutability: "ReadOnly", defaultValue: "", ocpp2Only: true },
  { ...cv("TxCtrlr", "TxStartPoint"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "PowerPathClosed", ocpp2Only: true },
  { ...cv("TxCtrlr", "TxStopPoint"), dataType: "MemberList", mutability: "ReadWrite", defaultValue: "PowerPathClosed", ocpp2Only: true },
  { ...cv("ReservationCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "true", ocpp2Only: true },
  { ...cv("SmartChargingCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "true", ocpp2Only: true },
  { ...cv("DisplayMessageCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "true", ocpp2Only: true },
  { ...cv("MonitoringCtrlr", "Enabled"), dataType: "boolean", mutability: "ReadOnly", defaultValue: "true", ocpp2Only: true },
];

const isValidValue = (dataType: DataType, value: string, valuesList?: string) => {
  if (valuesList && (dataType === "MemberList" || dataType === "SequenceList" || dataType === "OptionList")) {
    const allowed = valuesList.split(",");
    const items = value.split(",").map(item => item.trim()).filter(Boolean);
    if (dataType === "OptionList") return items.length === 1 && allowed.includes(items[0]);
    return new Set(items).size === items.length && items.every(item => allowed.includes(item));
  }
  switch (dataType) {
    case "integer":
      return /^-?\d+$/.test(value);
    case "decimal":
      return value.trim() !== "" && !Number.isNaN(Number(value));
    case "boolean":
      return value === "true" || value === "false";
    default:
      return true;
  }
};

export const sameRef = (a: ComponentRef, b: ComponentRef, va: VariableRef, vb: VariableRef) =>
  a.name === b.name &&
  (a.instance ?? "") === (b.instance ?? "") &&
  (a.evse?.id ?? 0) === (b.evse?.id ?? 0) &&
  (a.evse?.connectorId ?? 0) === (b.evse?.connectorId ?? 0) &&
  va.name === vb.name &&
  (va.instance ?? "") === (vb.instance ?? "");

export type SetStatus16 = "Accepted" | "Rejected" | "RebootRequired" | "NotSupported";
export type SetAttributeStatus = "Accepted" | "Rejected" | "UnknownComponent" | "UnknownVariable" | "NotSupportedAttributeType" | "RebootRequired";
export type GetAttributeStatus = "Accepted" | "Rejected" | "UnknownComponent" | "UnknownVariable" | "NotSupportedAttributeType";

export class ConfigurationStore {
  private readonly entries: ConfigEntry[];
  private listeners: Array<(entry: ConfigEntry) => void> = [];

  /**
   * @param settings 1.6 keys from the emulator settings (values and read-only flags win over
   *   the defaults; unknown keys are kept as 1.6-only string keys).
   */
  constructor(
    settings: SettingsKey[] = [],
    overrides: { identity?: string; numberOfConnectors?: number; evseIds?: number[]; fileTransferProtocols?: string[] } = {}
  ) {
    this.entries = DEFINITIONS.map(({ defaultValue, ...def }) => ({ ...def, value: defaultValue }));
    for (const setting of settings) {
      const entry = this.entries.find(e => e.key === setting.key);
      const value = String(setting.value ?? "").trim();
      if (entry) {
        entry.value = value;
        if (entry.mutability !== "WriteOnly") entry.mutability = setting.readonly ? "ReadOnly" : "ReadWrite";
      } else {
        this.entries.push({
          key: setting.key,
          component: { name: "VendorCtrlr" },
          variable: { name: setting.key },
          value,
          dataType: "string",
          mutability: setting.readonly ? "ReadOnly" : "ReadWrite",
          ocpp16Only: false,
        });
      }
    }
    if (overrides.identity !== undefined) this.findByRef({ name: "SecurityCtrlr" }, { name: "Identity" })!.value = overrides.identity;
    if (overrides.numberOfConnectors !== undefined) this.findByKey("NumberOfConnectors")!.value = String(overrides.numberOfConnectors);
    if (overrides.fileTransferProtocols) this.findByKey("SupportedFileTransferProtocols")!.value = overrides.fileTransferProtocols.join(",");
    for (const evseId of overrides.evseIds ?? []) {
      this.entries.push(
        { component: { name: "EVSE", evse: { id: evseId } }, variable: { name: "Available" }, value: "true", dataType: "boolean", mutability: "ReadOnly", ocpp2Only: true },
        { component: { name: "EVSE", evse: { id: evseId } }, variable: { name: "Power", instance: "Max" }, value: "22000", dataType: "decimal", mutability: "ReadOnly", ocpp2Only: true },
        { component: { name: "Connector", evse: { id: evseId, connectorId: 1 } }, variable: { name: "ConnectorType" }, value: "cType2", dataType: "string", mutability: "ReadOnly", ocpp2Only: true }
      );
    }
  }

  onChange(listener: (entry: ConfigEntry) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  all(): ConfigEntry[] {
    return this.entries.map(entry => ({ ...entry }));
  }

  findByKey(key: string) {
    return this.entries.find(entry => entry.key === key);
  }

  findByRef(component: ComponentRef, variable: VariableRef) {
    return this.entries.find(entry => sameRef(entry.component, component, entry.variable, variable));
  }

  /** The current value by 1.6 key, regardless of protocol. */
  get(key: string): string | undefined {
    return this.findByKey(key)?.value;
  }

  getNumber(key: string, fallback: number): number {
    const value = Number(this.get(key));
    return Number.isFinite(value) ? value : fallback;
  }

  getBoolean(key: string): boolean {
    return this.get(key) === "true";
  }

  // ---- OCPP 1.6 ----------------------------------------------------------------------------

  getConfiguration16(keys?: string[]) {
    const visible = this.entries.filter(entry => entry.key && !entry.ocpp2Only);
    const toKey = (entry: ConfigEntry) => ({
      key: entry.key!,
      readonly: entry.mutability === "ReadOnly",
      // Write-only secrets (AuthorizationKey) are never returned.
      ...(entry.mutability !== "WriteOnly" && { value: entry.value.slice(0, 500) }),
    });
    if (!keys || keys.length === 0) return { configurationKey: visible.map(toKey) };
    const configurationKey = keys.flatMap(key => {
      const entry = visible.find(e => e.key === key);
      return entry ? [toKey(entry)] : [];
    });
    const unknownKey = keys.filter(key => !visible.some(e => e.key === key));
    return { configurationKey, ...(unknownKey.length > 0 && { unknownKey }) };
  }

  changeConfiguration16(key: string, value: string): SetStatus16 {
    const entry = this.entries.find(e => e.key === key && !e.ocpp2Only);
    if (!entry) return "NotSupported";
    if (entry.mutability === "ReadOnly") return "Rejected";
    if (!this.accepts(entry, value)) return "Rejected";
    this.write(entry, value);
    return entry.rebootRequired ? "RebootRequired" : "Accepted";
  }

  /** Data type, allowed values and the 1.6 `<Key>MaxLength` companion key. */
  private accepts(entry: ConfigEntry, value: string) {
    if (!isValidValue(entry.dataType, value, entry.valuesList)) return false;
    const maxLength = entry.key ? this.get(`${entry.key}MaxLength`) : undefined;
    if (maxLength !== undefined && entry.dataType === "MemberList") {
      return value.split(",").filter(item => item.trim()).length <= Number(maxLength);
    }
    return true;
  }

  // ---- OCPP 2.x ----------------------------------------------------------------------------

  private find2x(component: ComponentRef, variable: VariableRef) {
    return this.entries.find(entry => !entry.ocpp16Only && sameRef(entry.component, component, entry.variable, variable));
  }

  private hasComponent(component: ComponentRef) {
    return this.entries.some(
      entry => !entry.ocpp16Only && entry.component.name === component.name && (entry.component.instance ?? "") === (component.instance ?? "")
    );
  }

  getVariable(component: ComponentRef, variable: VariableRef, attributeType = "Actual"): { status: GetAttributeStatus; value?: string } {
    const entry = this.find2x(component, variable);
    if (!entry) return { status: this.hasComponent(component) ? "UnknownVariable" : "UnknownComponent" };
    if (attributeType !== "Actual") return { status: "NotSupportedAttributeType" };
    if (entry.mutability === "WriteOnly") return { status: "Rejected" };
    return { status: "Accepted", value: entry.value };
  }

  setVariable(component: ComponentRef, variable: VariableRef, value: string, attributeType = "Actual"): SetAttributeStatus {
    const entry = this.find2x(component, variable);
    if (!entry) return this.hasComponent(component) ? "UnknownVariable" : "UnknownComponent";
    if (attributeType !== "Actual") return "NotSupportedAttributeType";
    if (entry.mutability === "ReadOnly") return "Rejected";
    if (!this.accepts(entry, value)) return "Rejected";
    this.write(entry, value);
    return entry.rebootRequired ? "RebootRequired" : "Accepted";
  }

  /** NotifyReport reportData for the 2.x device model. */
  report(filter?: (entry: ConfigEntry) => boolean) {
    return this.entries
      .filter(entry => !entry.ocpp16Only && (!filter || filter(entry)))
      .map(entry => ({
        component: entry.component,
        variable: entry.variable,
        variableAttribute: [
          {
            type: "Actual",
            ...(entry.mutability !== "WriteOnly" && { value: entry.value.slice(0, 2500) }),
            mutability: entry.mutability,
            persistent: true,
            constant: entry.mutability === "ReadOnly",
          },
        ],
        variableCharacteristics: {
          dataType: entry.dataType,
          ...(entry.valuesList && { valuesList: entry.valuesList }),
          supportsMonitoring: entry.dataType === "integer" || entry.dataType === "decimal",
        },
      }));
  }

  private write(entry: ConfigEntry, value: string) {
    entry.value = value;
    this.listeners.forEach(listener => listener({ ...entry }));
  }
}

const DERIVED_KEYS = ["NumberOfConnectors", "SupportedFileTransferProtocols"];

/**
 * The OCPP 1.6 configuration keys with their defaults, as the emulator settings list them. Keys
 * the charge point derives itself (connector count, file transfer protocols) and write-only
 * secrets are left out.
 */
export const defaultConfigurationKeys16 = (): SettingsKey[] =>
  DEFINITIONS.filter(
    def => def.key && !def.ocpp2Only && def.mutability !== "WriteOnly" && !DERIVED_KEYS.includes(def.key)
  ).map(def => ({ key: def.key!, readonly: def.mutability === "ReadOnly", value: def.defaultValue }));
